import { EXTERNAL_ROUTES } from '@/constants';
import { TableHeader } from '@/modules/common/components/TableHeader';
import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { ICohort, ICourse } from '@/types/course';
import { IEducationProviderAgreement } from '@/types/education-provider-agreement';
import {
  DeleteOutline,
  ExpandMore,
  PublishOutlined,
  UnpublishedOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Button, ButtonGroup, Checkbox, Menu, MenuItem } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { format, isValid } from 'date-fns';
import { useState } from 'react';
import { useCourseCategoryImage } from '../../hooks/useCourseCategoryImage';
import { CoursePublicationStatus } from '../CousePublicationStatus';

const columnHelper = createColumnHelper<ICourse>();

type GetListCoursesTableColumnsArgs = {
  onCourseEdit: (id: string) => void;
  onCourseDelete: (id: string) => void;
  onCourseUpdate: (args: { id: string; updatedData: Partial<ICourse> }) => void;
};

export function getListCoursesTableColumns({
  onCourseEdit,
  onCourseDelete,
  onCourseUpdate,
}: GetListCoursesTableColumnsArgs) {
  return [
    columnHelper.display({
      id: 'checkbox',
      header: () => null,
      cell: ({ row }) => {
        const isSelected = row.getIsSelected();

        return (
          <div className="flex flex-1 justify-center p-4 align-center">
            <Checkbox
              checked={isSelected}
              onChange={() => row.toggleSelected()}
            />
          </div>
        );
      },
    }),
    columnHelper.accessor('name', {
      header: () => (
        <TableHeader className="absolute left-0">Title</TableHeader>
      ),
      cell: ({ row: { original } }) => {
        const { categoryImage, category } =
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useCourseCategoryImage(original) ?? {};

        return (
          <div className="flex gap-3 items-center">
            {categoryImage && (
              <img
                src={categoryImage.src}
                width={categoryImage.width}
                height={categoryImage.height}
                alt={category?.name}
                className="object-cover w-20 h-20 rounded-md aspect-square"
              />
            )}
            <div className="flex flex-col gap-2">
              <div className="text-sm font-medium">{original.name}</div>
              {original.updatedAt && isValid(new Date(original.updatedAt)) && (
                <div className="text-xxs text-text">
                  Last update {format(original.updatedAt, 'PPPp')}
                </div>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor('cost.amount', {
      header: () => <TableHeader>PVP Price</TableHeader>,
      cell: ({
        row: {
          original: { cohorts = [] },
        },
      }) => {
        try {
          const maxPrice = Math.max(
            ...cohorts.map((cohort: ICohort) => cohort.cost?.amount ?? 0),
          );
          const maxPriceCurrency = cohorts.find(cohort => cohort.cost)?.cost
            ?.currency;

          if (maxPrice === 0) {
            return <span className="text-base text-mydra-black">Free</span>;
          }

          if (!maxPrice || !maxPriceCurrency) {
            return <span className="text-base text-mydra-black">-</span>;
          }

          return (
            <div>
              <span className="text-base text-mydra-black">
                {formatCurrencyValue(maxPrice.toString(), maxPriceCurrency)}
              </span>
            </div>
          );
        } catch (e) {
          console.error(e);
          return <span className="text-base text-mydra-black">-</span>;
        }
      },
    }),
    columnHelper.accessor('agreement.fees.discount', {
      header: () => <TableHeader>Price / Discount (%)</TableHeader>,
      cell: ({
        row: {
          original: { agreements = [], cohorts = [] },
        },
      }) => {
        try {
          if (!Array.isArray(agreements) || !agreements.length) {
            return <span className="text-base text-mydra-black">-</span>;
          }

          const maxDiscount = Math.max(
            ...(agreements as IEducationProviderAgreement[]).map(agreement => {
              return Math.max(...agreement.fees.map(fee => fee.discount));
            }),
          );

          const maxPrice = Math.max(
            ...cohorts.map((cohort: ICohort) => cohort.cost?.amount ?? 0),
          );

          const maxPriceCurrency = cohorts.find(cohort => cohort.cost)?.cost
            ?.currency;

          if (maxPrice === 0) {
            return <span className="text-base text-mydra-black">Free</span>;
          }

          if (!maxDiscount || !maxPrice || !maxPriceCurrency) {
            return <span className="text-base text-mydra-black">-</span>;
          }

          return (
            <div className="flex flex-col gap-2">
              <span className="text-base text-mydra-black">
                {formatCurrencyValue(
                  (maxPrice * (1 - maxDiscount)).toString(),
                  maxPriceCurrency,
                )}
              </span>
              <div className="text-xs">
                ({(maxDiscount * 100).toFixed(2)}% discount)
              </div>
            </div>
          );
        } catch (e) {
          console.error(e);
          return <span className="text-base text-mydra-black">-</span>;
        }
      },
    }),
    columnHelper.accessor('categories', {
      header: () => <TableHeader>Category</TableHeader>,
      cell: ({ row: { original } }) => {
        if (
          !Array.isArray(original.categories) ||
          !original.categories.length
        ) {
          return <span className="text-base text-mydra-black">-</span>;
        }

        return (
          <div>
            <span className="text-sm text-mydra-black">
              {original.categories[0].name}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor('published', {
      header: () => <TableHeader>Status</TableHeader>,
      cell: ({
        row: {
          original: { published },
        },
      }) => {
        return (
          <div>
            <CoursePublicationStatus published={!!published} />
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <TableHeader className="text-right">Actions</TableHeader>,
      cell: function ActionsCell({ row: { original } }) {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const handleClick = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
          setAnchorEl(null);
        };

        return (
          <div className="flex gap-2 ml-auto">
            <div className="ml-auto">
              <ButtonGroup
                disableElevation
                variant="outlined"
                size="small"
                aria-label="course actions button group"
              >
                <Button
                  onClick={() => onCourseEdit(original._id!)}
                  className="rounded-r-none border-lines"
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  onClick={handleClick}
                  aria-controls={open ? 'course-actions-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  className="rounded-l-none bg-gray-background border-dividers"
                >
                  <ExpandMore />
                </Button>
              </ButtonGroup>
              <Menu
                id="course-actions-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  list: {
                    'aria-labelledby': 'course-actions-button',
                  },
                }}
              >
                <MenuItem
                  className="border-b last-of-type:border-none"
                  onClick={() => {
                    handleClose();
                    window.open(
                      EXTERNAL_ROUTES.MARKETPLACE_COURSE_URL.replace(
                        ':id',
                        original._id!,
                      ),
                      '_blank',
                    );
                  }}
                >
                  <VisibilityOutlined className="mr-2" />
                  View course
                </MenuItem>
                <MenuItem
                  className="border-b last-of-type:border-none"
                  onClick={() => {
                    handleClose();
                    onCourseUpdate({
                      id: original._id!,
                      updatedData: { published: !original.published },
                    });
                  }}
                >
                  {!original.published ? (
                    <PublishOutlined className="mr-2" />
                  ) : (
                    <UnpublishedOutlined className="mr-2" />
                  )}
                  {original?.published ? 'Unpublish' : 'Publish'}
                </MenuItem>
                <MenuItem
                  className="border-b last-of-type:border-none"
                  onClick={() => {
                    handleClose();
                    onCourseDelete(original._id!);
                  }}
                >
                  <DeleteOutline className="mr-2" />
                  Delete
                </MenuItem>
              </Menu>
            </div>
          </div>
        );
      },
    }),
  ];
}
